import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Form, Button } from "react-bootstrap";
import { uploadReportAction } from "../../../../store/actions/ReportAction";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import logo from "../../../../assets/images/logo.png";
import { showErrorAlert } from "../../../../services/ErrorHandlingService";
import FormSuccessMessage from "../../Common/FormSuccessMessage";

const ReportUpload = () => {
  const dispatch = useDispatch();
  const { patientId: encodedPatientId, doctorId: encodedDoctorId } =
    useParams();

  // Decode base64 patientId
  const patientId = React.useMemo(() => {
    if (!encodedPatientId) return null;
  
    try {
      return atob(encodedPatientId);
    } catch {
      // If not base64, return as is
      return encodedPatientId;
    }
  }, [encodedPatientId]);

  // Decode base64 doctorId
  const doctorId = React.useMemo(() => {
    if (!encodedDoctorId) return null;
  
    try {
      return atob(encodedDoctorId);
    } catch {
      // If not base64, return as is
      return encodedDoctorId;
    }
  }, [encodedDoctorId]);

  const [formData, setFormData] = useState({
    reportTitle: "",
    reportdescription: "",
    reportDate: new Date().toISOString().split("T")[0],
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewList, setPreviewList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // MULTIPLE FILE SELECT & PREVIEW
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);

    const previews = files.map((file) => ({
      name: file.name,
      type: file.type.startsWith("image/")
        ? "image"
        : file.type === "application/pdf"
          ? "pdf"
          : "other",
      url: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
    }));

    setPreviewList(previews);
  };

  React.useEffect(() => {
    return () => {
      previewList.forEach((file) => {
        if (file.url) {
          URL.revokeObjectURL(file.url);
        }
      });
    };
  }, [previewList]);
 
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedFiles.length === 0) {
      showErrorAlert("Please select report files");
      return;
    }

    setLoading(true);

    try {
      // BUILD FORMDATA
      const form = new FormData();
      form.append("patient_id", patientId);
      form.append("doctor_id", doctorId || "");
      form.append("report_title", formData.reportTitle);
      form.append("report_date", formData.reportDate);
      form.append("report_description", formData.reportdescription);

      selectedFiles.forEach((file) => {
        form.append("report_file[]", file);
      });

      const response = await dispatch(uploadReportAction(form));
      console.log("UPLOAD RESPONSE:", response);

      if (response?.status === 200) {
        setIsSubmitted(true);
      }
    } catch (error) {
      showErrorAlert("Failed to upload report: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Validate decoded patientId
  if (!patientId) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">
          Invalid patient ID format. Please make sure you have the correct URL.
        </div>
      </div>
    );
  }

  // Show success message if form was submitted successfully
  if (isSubmitted) {
    return (
      <FormSuccessMessage
        title="Form submitted successfully. Thank you!"
        message="Your report has been uploaded successfully."
      />
    );
  }

  return (
    <div className="h-100 p-meddle">
      <div className="container h-100">
        <div className="row justify-content-center h-100 align-items-center">
          <div className="col-md-12 col-lg-6"></div>

          <div className="row justify-content-center align-items-center">
            <div className="col-md-12 col-lg-6 align-items-center">
              <img
                src={logo}
                alt="Logo"
                style={{ width: "100%", height: "auto" }}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-xl-12 col-xxl-12">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title">Upload Report</h4>
                </div>
                <div className="card-body">
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Report Title</Form.Label>
                      <Form.Control
                        type="text"
                        name="reportTitle"
                        value={formData.reportTitle}
                        onChange={handleInputChange}
                        // required
                        placeholder="Enter report title"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Report Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="reportDate"
                        value={formData.reportDate}
                        onChange={handleInputChange}
                      // required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Upload Report File<span style={{ color: "red" }}>*</span></Form.Label>
                      <Form.Control
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                        required
                      />
                      <Form.Text className="text-muted">
                        Supported formats: JPG, PNG, PDF
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Report Description</Form.Label>
                      <Form.Control
                        type="text"
                        name="reportdescription"
                        value={formData.reportdescription}
                        onChange={handleInputChange}
                        placeholder="Enter Report Description"
                      />
                    </Form.Group>

                    {/* Preview Section */}
                    {previewList.length > 0 && (
                      <div className="mb-3">
                        <h6>Preview:</h6>

                        {previewList.map((file, i) => (
                          <div key={i} className="mb-2">
                            {file.type === "image" ? (
                              <img
                                src={file.url}
                                alt="preview"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "200px",
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <div className="alert alert-info">
                                PDF selected: {file.name}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="d-flex justify-content-end">
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? "Uploading..." : "Upload Report"}
                      </Button>
                    </div>
                  </Form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ReportUpload.propTypes = {
  patientId: PropTypes.string,
  doctorId: PropTypes.string,
};

export default ReportUpload;
